import type { CSSProperties } from 'react'

/**
 * Design tokens for public shop storefronts (P0).
 * Additive: missing values keep the legacy look.
 */

export type ShopSurfaceBase = 'default' | 'warm' | 'cool' | 'ink'
export type ShopMotionDefault = 'none' | 'fade-up'
/** Product tile chrome for storefront catalog / product grids. */
export type ShopCatalogCardStyle = 'classic' | 'soft' | 'dense' | 'flat'
export type ShopSectionSpacing = 'default' | 'compact' | 'comfortable' | 'spacious'
export type ShopSectionSurface = 'default' | 'muted' | 'soft' | 'brand' | 'contrast'
export type ShopSectionMotion = 'none' | 'fade-up' | 'fade-in'
/** Content width; bleed = edge-to-edge (no side gutter). */
export type ShopSectionWidth = 'default' | 'narrow' | 'wide' | 'full' | 'bleed'

export type ShopDesignTokens = {
  /** Display / heading font (falls back to website font_family). */
  heading_font?: string
  /** Optional body UI font; CMS Chinese-safe body stack still used for rich text. */
  body_font?: string
  /** Page canvas background. */
  surface_base?: ShopSurfaceBase
  /**
   * Product card chrome: classic bordered tile (default), soft Muji-like wells,
   * dense retail tiles, or flat image+text with almost no chrome.
   */
  catalog_card_style?: ShopCatalogCardStyle
  /** Default entrance motion when a section omits `motion`. */
  motion_default?: ShopMotionDefault
  /**
   * Transparent/dark sticky header over a cinematic hero (additive).
   * When unset, keeps the legacy light header tint.
   */
  header_overlay?: 'dark'
  /** Always show shop name beside logo (default hides name below xl when logo is set). */
  header_show_name?: boolean
  /** Compact header mark — default keeps the large legacy logo. */
  header_logo_size?: 'sm' | 'md' | 'lg'
  /** Optional header CTA (e.g. “免費預約諮詢”). */
  header_cta_text?: string
  header_cta_link?: string
  /** Optional HTML brand label (sanitized); falls back to shop name. */
  header_brand_html?: string
  /** Hide cart control in the storefront header. */
  header_hide_cart?: boolean
  /**
   * Optional scoped custom CSS for this shop storefront only (P2).
   * Sanitized / size-capped on save; never injected as a global site stylesheet elsewhere.
   */
  custom_css?: string
}

export type ShopFontPairId =
  | ''
  | 'editorial-serif'
  | 'modern-sans'
  | 'display-playfair'
  | 'classic-georgia'
  | 'soft-romance'

export const SHOP_FONT_PAIRS: Array<{
  id: ShopFontPairId
  labelKey: string
  heading: string
  body: string
  /** Also written to legacy font_family for brand/logo. */
  fontFamily: string
}> = [
  {
    id: '',
    labelKey: 'website.fontThemeDefault',
    heading: '',
    body: '',
    fontFamily: '',
  },
  {
    id: 'editorial-serif',
    labelKey: 'website.fontPairEditorial',
    heading: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
    body: "Inter, system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei UI', sans-serif",
    fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
  },
  {
    id: 'modern-sans',
    labelKey: 'website.fontPairModern',
    heading: "'Inter', system-ui, -apple-system, sans-serif",
    body: "'Inter', system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei UI', sans-serif",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  {
    id: 'display-playfair',
    labelKey: 'website.fontPairPlayfair',
    heading: "'Playfair Display', Georgia, serif",
    body: "Inter, system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei UI', sans-serif",
    fontFamily: "'Playfair Display', Georgia, serif",
  },
  {
    id: 'classic-georgia',
    labelKey: 'website.fontPairGeorgia',
    heading: "Georgia, 'Times New Roman', serif",
    body: "Georgia, 'Times New Roman', 'Songti SC', 'STSong', serif",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  {
    id: 'soft-romance',
    labelKey: 'website.fontPairRomance',
    heading: "'Playfair Display', Georgia, serif",
    body: "'Cormorant Garamond', Georgia, 'Songti SC', serif",
    fontFamily: "'Playfair Display', Georgia, serif",
  },
]

const SURFACE_BASE_BG: Record<ShopSurfaceBase, string> = {
  default: '#f8fafc', // slate-50 — current default
  warm: '#faf7f2',
  cool: '#f1f5f9',
  ink: '#0f172a',
}

/**
 * Sanitize tenant custom CSS for storefront scoping.
 * Strips HTML breakouts, @import, expression(), javascript URLs, and caps length.
 */
export const SHOP_CUSTOM_CSS_MAX_CHARS = 8000

export function sanitizeShopCustomCss(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  let css = raw.replace(/\0/g, '').trim()
  if (!css) return ''
  if (css.length > SHOP_CUSTOM_CSS_MAX_CHARS) {
    css = css.slice(0, SHOP_CUSTOM_CSS_MAX_CHARS)
  }
  // Prevent breaking out of <style>…</style> (and any HTML injection).
  css = css.replace(/<\/?style\b[^>]*>/gi, '')
  css = css.replace(/<\/?[a-zA-Z!][^>]*>/g, '')
  css = css.replace(/</g, '')
  // Block remote/stylesheet imports and script-like vectors.
  css = css.replace(/@import\b[^;]*;?/gi, '')
  css = css.replace(/@charset\b[^;]*;?/gi, '')
  css = css.replace(/@namespace\b[^;]*;?/gi, '')
  css = css.replace(/expression\s*\(/gi, '/* blocked */')
  css = css.replace(/url\s*\(\s*['"]?\s*javascript:[^)]*\)/gi, 'url(about:blank)')
  css = css.replace(/-moz-binding\s*:/gi, '/* blocked */')
  css = css.replace(/behavior\s*:/gi, '/* blocked */')
  return css.trim()
}

function scopeSelectorList(selectors: string): string {
  return selectors
    .split(',')
    .map((s) => {
      const t = s.trim()
      if (!t) return t
      if (t.startsWith('.shop-storefront-root')) return t
      if (t === 'body' || t === 'html' || t === ':root') return '.shop-storefront-root'
      return `.shop-storefront-root ${t}`
    })
    .join(', ')
}

function findMatchingBrace(css: string, openIdx: number): number {
  let depth = 0
  for (let i = openIdx; i < css.length; i++) {
    const ch = css.charAt(i)
    if (ch === '{') depth++
    else if (ch === '}') {
      depth--
      if (depth === 0) return i
    }
  }
  return -1
}

/** Recursively scope rule selectors, including those inside @media / @supports. */
function scopeCssChunk(css: string): string {
  let out = ''
  let i = 0
  while (i < css.length) {
    while (i < css.length && /\s/.test(css.charAt(i))) {
      out += css.charAt(i)
      i++
    }
    if (i >= css.length) break

    const open = css.indexOf('{', i)
    if (open < 0) {
      out += css.slice(i)
      break
    }
    const prelude = css.slice(i, open).trim()
    const close = findMatchingBrace(css, open)
    if (close < 0) {
      out += css.slice(i)
      break
    }
    const body = css.slice(open + 1, close)

    if (prelude.startsWith('@')) {
      const atName = prelude.slice(1).split(/[\s(]/)[0]?.toLowerCase() || ''
      if (atName === 'keyframes' || atName === '-webkit-keyframes' || atName === 'font-face' || atName === 'property') {
        // Leave animation/font payloads unscoped (selectors inside keyframes are percentages).
        out += `${prelude} {${body}}`
      } else if (
        atName === 'media' ||
        atName === 'supports' ||
        atName === 'container' ||
        atName === 'layer' ||
        atName === 'document'
      ) {
        out += `${prelude} {${scopeCssChunk(body)}}`
      } else {
        // Unknown at-rule: drop to avoid unscoped leakage.
        out += `/* blocked @${atName} */`
      }
    } else {
      out += `${scopeSelectorList(prelude)} {${body}}`
    }
    i = close + 1
  }
  return out
}

/** Scope custom CSS so it only affects `.shop-storefront-root`. */
export function scopeShopCustomCss(css: string): string {
  const clean = sanitizeShopCustomCss(css)
  if (!clean) return ''
  return scopeCssChunk(clean).trim()
}

export function parseShopDesignTokens(raw: unknown): ShopDesignTokens {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const o = raw as Record<string, unknown>
  const tokens: ShopDesignTokens = {}
  if (typeof o.heading_font === 'string' && o.heading_font.trim()) {
    tokens.heading_font = o.heading_font.trim()
  }
  if (typeof o.body_font === 'string' && o.body_font.trim()) {
    tokens.body_font = o.body_font.trim()
  }
  const surface = String(o.surface_base || '').trim()
  if (surface === 'warm' || surface === 'cool' || surface === 'ink' || surface === 'default') {
    tokens.surface_base = surface
  }
  const cardStyle = String(o.catalog_card_style || '').trim()
  if (cardStyle === 'classic' || cardStyle === 'soft' || cardStyle === 'dense' || cardStyle === 'flat') {
    tokens.catalog_card_style = cardStyle
  }
  const motion = String(o.motion_default || '').trim()
  if (motion === 'fade-up' || motion === 'none') {
    tokens.motion_default = motion
  }
  if (String(o.header_overlay || '').trim() === 'dark') {
    tokens.header_overlay = 'dark'
  }
  if (o.header_show_name === true || o.header_show_name === 'true' || o.header_show_name === 1) {
    tokens.header_show_name = true
  }
  const logoSize = String(o.header_logo_size || '').trim()
  if (logoSize === 'sm' || logoSize === 'md' || logoSize === 'lg') {
    tokens.header_logo_size = logoSize
  }
  if (typeof o.header_cta_text === 'string' && o.header_cta_text.trim()) {
    tokens.header_cta_text = o.header_cta_text.trim().slice(0, 80)
  }
  if (typeof o.header_cta_link === 'string' && o.header_cta_link.trim()) {
    tokens.header_cta_link = o.header_cta_link.trim().slice(0, 500)
  }
  if (typeof o.header_brand_html === 'string' && o.header_brand_html.trim()) {
    tokens.header_brand_html = o.header_brand_html.trim().slice(0, 240)
  }
  if (o.header_hide_cart === true || o.header_hide_cart === 'true' || o.header_hide_cart === 1) {
    tokens.header_hide_cart = true
  }
  if (typeof o.custom_css === 'string' && o.custom_css.trim()) {
    const cleaned = sanitizeShopCustomCss(o.custom_css)
    if (cleaned) tokens.custom_css = cleaned
  }
  return tokens
}

export function matchFontPairId(tokens: ShopDesignTokens, fontFamily?: string | null): ShopFontPairId {
  const heading = (tokens.heading_font || fontFamily || '').trim()
  const body = (tokens.body_font || '').trim()
  for (const pair of SHOP_FONT_PAIRS) {
    if (!pair.id) continue
    if (pair.heading === heading && (!body || pair.body === body)) return pair.id
  }
  return ''
}

/** CSS custom properties for the storefront root. */
export function shopDesignTokenCssVars(opts: {
  primaryColor?: string | null
  secondaryColor?: string | null
  fontFamily?: string | null
  designTokens?: ShopDesignTokens | null
}): CSSProperties {
  const primary = (opts.primaryColor || '').trim() || '#4f46e5'
  const secondary = (opts.secondaryColor || '').trim() || '#818cf8'
  const tokens = opts.designTokens || {}
  const heading =
    (tokens.heading_font || '').trim() ||
    (opts.fontFamily || '').trim() ||
    "Georgia, 'Times New Roman', serif"
  const body =
    (tokens.body_font || '').trim() ||
    "Inter, system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei UI', sans-serif"
  const surface = tokens.surface_base || 'default'
  const pageBg = SURFACE_BASE_BG[surface]

  return {
    ['--shop-primary' as string]: primary,
    ['--shop-secondary' as string]: secondary,
    ['--shop-heading-font' as string]: heading,
    ['--shop-body-font' as string]: body,
    ['--shop-page-bg' as string]: pageBg,
    ['--shop-surface-muted' as string]:
      surface === 'ink' ? 'color-mix(in srgb, white 8%, transparent)' : '#f1f5f9',
    ['--shop-surface-soft' as string]:
      surface === 'ink' ? 'color-mix(in srgb, white 6%, transparent)' : '#faf7f2',
    ['--shop-surface-brand' as string]: `color-mix(in srgb, ${primary} 10%, white)`,
    ['--shop-surface-contrast' as string]: surface === 'ink' ? '#1e293b' : '#0f172a',
    ['--shop-text-on-contrast' as string]: '#f8fafc',
    backgroundColor: pageBg,
    color: surface === 'ink' ? '#e2e8f0' : undefined,
  }
}

export function parseSectionSpacing(raw: unknown): ShopSectionSpacing {
  const v = String(raw || 'default').trim().toLowerCase()
  if (v === 'compact' || v === 'comfortable' || v === 'spacious') return v
  return 'default'
}

export function parseSectionSurface(raw: unknown): ShopSectionSurface {
  const v = String(raw || 'default').trim().toLowerCase()
  if (v === 'muted' || v === 'soft' || v === 'brand' || v === 'contrast') return v
  return 'default'
}

export function parseSectionMotion(
  raw: unknown,
  siteDefault?: ShopMotionDefault | null,
): ShopSectionMotion {
  const v = String(raw || '').trim().toLowerCase()
  if (v === 'fade-up' || v === 'fade-in' || v === 'none') return v
  if (siteDefault === 'fade-up') return 'fade-up'
  return 'none'
}

export function parseSectionWidth(raw: unknown): ShopSectionWidth {
  const v = String(raw || 'default').trim().toLowerCase()
  if (v === 'narrow' || v === 'wide' || v === 'full' || v === 'bleed') return v
  return 'default'
}

/** Extra vertical padding beyond the stack gap (additive; default = no change). */
export function shopSectionSpacingClass(spacing: ShopSectionSpacing): string {
  switch (spacing) {
    case 'compact':
      return 'py-2 sm:py-3'
    case 'comfortable':
      return 'py-8 sm:py-10'
    case 'spacious':
      return 'py-12 sm:py-16'
    default:
      return ''
  }
}

export function shopSectionSurfaceClass(surface: ShopSectionSurface): string {
  switch (surface) {
    case 'muted':
      return 'bg-[color:var(--shop-surface-muted)]'
    case 'soft':
      return 'bg-[color:var(--shop-surface-soft)]'
    case 'brand':
      return 'bg-[color:var(--shop-surface-brand)]'
    case 'contrast':
      return 'bg-[color:var(--shop-surface-contrast)] text-[color:var(--shop-text-on-contrast)]'
    default:
      return ''
  }
}

export function shopSectionMotionClass(motion: ShopSectionMotion): string {
  if (motion === 'fade-up') return 'shop-reveal shop-reveal-up'
  if (motion === 'fade-in') return 'shop-reveal shop-reveal-in'
  return ''
}

/** Outer horizontal gutter; bleed removes side padding for edge-to-edge media. */
export function shopSectionGutterClass(width: ShopSectionWidth): string {
  if (width === 'bleed') return 'px-0'
  return 'px-6 sm:px-8'
}

/** Inner max-width for section content. */
export function shopSectionInnerMaxClass(width: ShopSectionWidth): string {
  switch (width) {
    case 'narrow':
      return 'max-w-3xl'
    case 'wide':
      return 'max-w-[90rem]'
    case 'full':
    case 'bleed':
      return 'max-w-none'
    default:
      return 'max-w-7xl'
  }
}

/** Resolve chrome classes from section config + site tokens. */
export function shopSectionChromeClasses(
  cfg: Record<string, unknown>,
  designTokens?: ShopDesignTokens | null,
): string {
  const spacing = parseSectionSpacing(cfg.spacing)
  const surface = parseSectionSurface(cfg.surface)
  const motion = parseSectionMotion(cfg.motion, designTokens?.motion_default)
  return [
    shopSectionSpacingClass(spacing),
    shopSectionSurfaceClass(surface),
    shopSectionMotionClass(motion),
  ]
    .filter(Boolean)
    .join(' ')
}
